import React, { Dispatch, SetStateAction, useEffect } from 'react';
//redux
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/ducks';
import { Creators as Actions } from "store/ducks/quotesDashboard";
//components
import QuoteLayoutIndicator from 'pages/Quotes/components/QuoteLayoutIndicator';
import QuotationFilters from 'pages/Quotes/components/QuotationFilters';
import { Column, Row } from 'components/Layout/Grid';
import Text from "components/Text";
//interfaces
import { QuotePanelProps } from './interface';
//models
import { QuoteDashboardIndicator } from 'models/QuoteDashboardIndicator';
import { IUser } from 'models/User';
//services
import QuoteIndicatorServices from 'services/quote-indicator.service';
import PreOrderIndicatorServices from "services/pre-order-indicator.service";
//context
import { FiltersHeaderContextProvider } from 'contexts/FiltersHeader';
//styles
import { colors } from 'pages/Quotes/styles/colors';

const QuotePanel: React.FC<QuotePanelProps> = (props) => {
  const {
    orders,
    setOrders,
    getPreOrder,
    updateFilter,
    pendingMoney,
    registerMoney,
    setStatusViveo,
    setOrderStatus,
    availableQuotes,
    setPendingMoney,
    getBionexoQuote,
    setRegisterMoney,
    setStatusBionexo,
    setQuoteCategory,
    setSelectedRadio,
    expiredUnanswered,
    setAvailableQuotes,
    setExpiredUnanswered,
    setOrderRadioSelected,
    setUseStateMainIndicator,
    sentAndExpired,
    setSentAndExpired,
    resetCardSelection
  } = props;

  const globalFilter = useSelector((stateFilter: RootState) => stateFilter.quotesDashboard);
  const user = useSelector((state: { user: IUser; }) => state.user);
  const indicatorService = new QuoteIndicatorServices();
  const bcgOnly = process.env.REACT_APP_BCG_ONLY === 'true';
  const orderIndicatorService = new PreOrderIndicatorServices();

  const dispatch = useDispatch();
  
  const onReload = (
    status: number[], 
    supplierStatus: number[], 
    data: QuoteDashboardIndicator, 
    setData: Dispatch<SetStateAction<QuoteDashboardIndicator>>
  ) => {
    if (status.includes(0) && supplierStatus.includes(0)) {
      setUseStateMainIndicator(indicatorService.AvailableForQuote(
        globalFilter?.subsidiary,
        globalFilter?.sellerId,
        globalFilter?.productCategories,
        status,
        supplierStatus,
        globalFilter?.startDate,
        globalFilter?.endDate,
        globalFilter?.calendarFilter,
        globalFilter?.origin
      ),
        availableQuotes,
        setAvailableQuotes);
    } else {
      setUseStateMainIndicator(indicatorService.DefaultIndicator(
        globalFilter?.subsidiary,
        globalFilter?.sellerId,
        globalFilter?.productCategories,
        status,
        supplierStatus,
        globalFilter?.startDate,
        globalFilter?.endDate,
        globalFilter?.calendarFilter,
        globalFilter?.origin,
        globalFilter?.sentExpiredFilter
      ),
        data,
        setData);
      }
  };

  const onOrderIndicatorReload = (
    status: number[], 
    data: QuoteDashboardIndicator, 
    setData: Dispatch<SetStateAction<QuoteDashboardIndicator>>
  ) => {
    setUseStateMainIndicator(orderIndicatorService.GetTotalPreOrders(
      bcgOnly ? user.buBCG?.split(",") : user.buBCG?.split(",") ?? globalFilter?.subsidiary,
      user.representative?.split(",") ?? globalFilter?.sellerId,
      status,
      globalFilter?.startDate,
      globalFilter?.endDate,
      globalFilter?.origin
    ),
      data,
      setData);
  };

  useEffect(() => {
    if (resetCardSelection !== 0) {
      dispatch(Actions.setSelectedFilterCard(null));
    }
  }, [resetCardSelection, dispatch]);

  return (
    <>
      <Row className="mt-3 d-flex align-center flex-sm-wrap">
        <Column className="w-50 w-md-100 mb-sm-2">
          <Text
            size={24}
            className="main-title main-title-dash font-bold w-sm-100"
            color={colors.greenDarkViveo}
          >
            Grid de Cotações/Pedidos
          </Text>
        </Column>
      </Row>

      <Row className="py-3 pt-md-0 flex-lg-column">
        <Column className="w-100 w-lg-100 px-3 pr-3 pt-3 bg-primary pl-sm-3 pl-md-3 pl-lg-3 dashboard-panel">
          <FiltersHeaderContextProvider>
            <QuotationFilters updateFilter={(filter) => updateFilter(filter)} />
          </FiltersHeaderContextProvider>

          <Row className="indicator-container">
            <QuoteLayoutIndicator
              key="availableQuotes"
              data={availableQuotes}
              getPreOrder={getPreOrder}
              getBionexoQuote={getBionexoQuote}
              setStatusViveo={setStatusViveo}
              setOrderStatus={setOrderStatus}
              setStatusBionexo={setStatusBionexo}
              setQuoteCategory={setQuoteCategory}
              setSelectedRadio={setSelectedRadio}
              setOrderRadioSelected={setOrderRadioSelected}
              onReload={() => onReload([0], [0], availableQuotes, setAvailableQuotes)}
            />

            <QuoteLayoutIndicator
              key="registerMoney"
              data={registerMoney}
              getPreOrder={getPreOrder}
              getBionexoQuote={getBionexoQuote}
              setStatusViveo={setStatusViveo}
              setOrderStatus={setOrderStatus}
              setStatusBionexo={setStatusBionexo}
              setQuoteCategory={setQuoteCategory}
              setSelectedRadio={setSelectedRadio}
              setOrderRadioSelected={setOrderRadioSelected}
              onReload={() => onReload([0, 1, 2, 3, 4, 6, 8], [1, 2, 3, 5, 6, 11, 13], registerMoney, setRegisterMoney)}
            />

            <QuoteLayoutIndicator
              key="pendingMoney"
              data={pendingMoney}
              getPreOrder={getPreOrder}
              tooltipContent={"Clique para filtrar as cotações enviadas, mas que apresentaram erro"}
              getBionexoQuote={getBionexoQuote}
              setStatusViveo={setStatusViveo}
              setOrderStatus={setOrderStatus}
              setStatusBionexo={setStatusBionexo}
              setQuoteCategory={setQuoteCategory}
              setSelectedRadio={setSelectedRadio}
              setOrderRadioSelected={setOrderRadioSelected}
              onReload={() => onReload([4, 0, 2, 8], [1], pendingMoney, setPendingMoney)}
            />

            <QuoteLayoutIndicator
              key="orders"
              data={orders}
              getPreOrder={getPreOrder}
              setStatusViveo={setStatusViveo}
              setOrderStatus={setOrderStatus}
              setStatusBionexo={setStatusBionexo}
              setQuoteCategory={setQuoteCategory}
              setSelectedRadio={setSelectedRadio}
              setOrderRadioSelected={setOrderRadioSelected}
              onReload={() => onOrderIndicatorReload([0, 1], orders, setOrders)}
            />

            <QuoteLayoutIndicator
              key="sentAndExpired"
              data={sentAndExpired}
              getPreOrder={getPreOrder}
              setStatusViveo={setStatusViveo}
              setOrderStatus={setOrderStatus}
              getBionexoQuote={getBionexoQuote}
              setStatusBionexo={setStatusBionexo}
              setQuoteCategory={setQuoteCategory}
              setSelectedRadio={setSelectedRadio}
              setOrderRadioSelected={setOrderRadioSelected}
              onReload={() => onReload([0, 1, 2, 3, 4, 6, 8], [1, 2, 3, 5, 6, 11, 13], sentAndExpired, setSentAndExpired)}
              tooltipContent={"Clique para filtrar cotações enviadas, mas que já passaram do prazo de expiração"}
            />

            <QuoteLayoutIndicator
              key="expiredUnanswered"
              data={expiredUnanswered}
              getPreOrder={getPreOrder}
              getBionexoQuote={getBionexoQuote}
              setStatusViveo={setStatusViveo}
              setOrderStatus={setOrderStatus}
              setStatusBionexo={setStatusBionexo}
              setQuoteCategory={setQuoteCategory}
              setSelectedRadio={setSelectedRadio}
              setOrderRadioSelected={setOrderRadioSelected}
              onReload={() => onReload([2], [7], expiredUnanswered, setExpiredUnanswered)}
            />
          </Row>
        </Column>
      </Row>
    </>
  );
};

export default QuotePanel;